import { Lightning } from '@lightningjs/sdk';

export default class FlareShader extends Lightning.shaders.WebGLDefaultShader {
  constructor(ctx) {
    super(ctx);
    this._lineX = 0.0;
    this._radius = 0.0;
    this._rotation = 0.0;
  }

  get lineX() {
    return this._lineX;
  }

  set lineX(value) {
    this._lineX = value;
    this.redraw();
  }

  get radius() {
    return this._radius;
  }

  set radius(value) {
    this._radius = value;
    this.redraw();
  }

  get rotation() {
    return this._rotation;
  }

  set rotation(value) {
    this._rotation = value;
    this.redraw();
  }

  setupUniforms(operation) {
    super.setupUniforms(operation);

    const { ctx, gl } = this;
    const { w, h } = ctx.stage;
    const { _w, _h } = operation.shaderOwner;
    const renderPrecision = this.ctx.stage.getRenderPrecision();

    this._setUniform('uLineX', this._lineX, gl.uniform1f);
    this._setUniform('uRadius', this._radius, gl.uniform1f);
    this._setUniform('uRotation', this._rotation, gl.uniform1f);
    this._setUniform('uResolution', [w, h], gl.uniform2fv);
    this._setUniform('projection', this._getProjection(operation), gl.uniform2fv, false);
    this._setUniform('uSize', new Float32Array([_w * renderPrecision, _h * renderPrecision]), this.gl.uniform2fv);
  }
}

FlareShader.vertexShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif

    attribute vec2 aVertexPosition;
    attribute vec2 aTextureCoord;
    attribute vec2 aVertex;
    attribute vec4 aColor;

    varying vec4 vColor;
    varying vec2 vTextureCoord;

    uniform vec2 projection;

    void main(void) {
        gl_Position = vec4(aVertexPosition.x * projection.x - 1.0, aVertexPosition.y * -abs(projection.y) + 1.0, 0.0, 1.0);
        gl_Position.y = -sign(projection.y) * gl_Position.y;

        vTextureCoord = aTextureCoord;
        vColor = aColor;
    }
`;

FlareShader.fragmentShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif

    #define PI 3.14159

    varying vec4 vColor;
    varying vec2 vTextureCoord;

    uniform vec2 uResolution;
    uniform vec2 uSize;
    uniform float uLineX;
    uniform float uRadius;
    uniform float uRotation;

    float boxDist(vec2 p, vec2 size, float radius) {
      size -= vec2(radius);
      vec2 d = abs(p) - size;
      return min(max(d.x, d.y), 0.0) + length(max(d, 0.0)) - radius;
    }

    void main() {
      float angle = radians(uRotation);
      float cosA = cos(angle);
      float sinA = sin(angle);
      mat2 rotation = mat2(cosA, -sinA, sinA, cosA);

      vec2 uv = ((gl_FragCoord.xy / uResolution.xy) - vec2(0.5)) * rotation + vec2(0.5);

      vec2 halfRes = 0.5 * uSize.xy;
      float mask = boxDist(vTextureCoord.xy * uSize - halfRes, halfRes - 0.005, uRadius);

      float line = uLineX / uResolution.x;
      float distanceToLine = uv.x - line;
      float absoluteDistance = pow(pow(distanceToLine, 2.0), 0.5);

      gl_FragColor = mix(vec4(0.0), vec4(vec3(max(1.0 - absoluteDistance, 0.75)), 1.0) * vColor, clamp(-mask, 0.0, 1.0));
    }
`;
